/**
 * H3 utilities: viewport → cells, density, snap, area-query helpers.
 * Pure functions wrapping the h3-js v4 API; safe to use server-side too.
 */

import {
  latLngToCell,
  cellToBoundary,
  cellToLatLng,
  polygonToCells,
} from 'h3-js';
import type { NormalizedSaEvent } from '@vtak/shared';

/** Render budget — beyond this we skip the overlay rather than locking the UI */
export const MAX_CELLS = 4000;

interface Box {
  north: number;
  south: number;
  east: number;
  west: number;
}

/**
 * Approximate hex area in km² at each H3 resolution.
 * Source: H3 docs (Average Hexagon Area by Resolution).
 */
const HEX_AREA_KM2: Record<number, number> = {
  0: 4_250_547,
  1: 607_221,
  2: 86_746,
  3: 12_393,
  4: 1_770,
  5: 252.9,
  6: 36.13,
  7: 5.161,
  8: 0.7373,
  9: 0.1053,
  10: 0.01505,
  11: 0.002150,
  12: 0.0003072,
  13: 0.00004388,
  14: 0.000006270,
  15: 0.0000008954,
};

/** Cheap estimate of cell count — avoids calling polygonToCells on millions of cells */
function estimateCellsForBox(box: Box, resolution: number): number {
  const latSpan = Math.max(0, box.north - box.south);
  const lngSpan = Math.max(0, box.east - box.west);
  const midLatRad = ((box.north + box.south) / 2) * (Math.PI / 180);
  const areaKm2 = latSpan * lngSpan * 111 * 111 * Math.max(Math.cos(midLatRad), 0);
  const cellArea = HEX_AREA_KM2[resolution] ?? 0.01;
  return areaKm2 / cellArea;
}

/** Containing-cell snap for a single coordinate */
export function snapToHexCenter(
  lat: number,
  lng: number,
  resolution: number,
): { lat: number; lng: number } {
  const cell = latLngToCell(lat, lng, resolution);
  const [snappedLat, snappedLng] = cellToLatLng(cell);
  return { lat: snappedLat, lng: snappedLng };
}

/** Snap a list of CoT events to hex centers without mutating the originals */
export function snapEvents(
  events: NormalizedSaEvent[],
  resolution: number,
): NormalizedSaEvent[] {
  return events.map((evt) => {
    const { lat, lng } = snapToHexCenter(evt.lat, evt.lon, resolution);
    return { ...evt, lat, lon: lng };
  });
}

/** H3 cells covering a 2D map viewport, with an overLimit flag when the cap was hit */
export function cellsForBounds(
  bounds: google.maps.LatLngBounds,
  resolution: number,
): { cells: string[]; overLimit: boolean } {
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  return cellsForBox(
    { north: ne.lat(), south: sw.lat(), east: ne.lng(), west: sw.lng() },
    resolution,
  );
}

/** H3 cells covering a rectangular lat/lng box */
export function cellsForBox(
  box: Box,
  resolution: number,
): { cells: string[]; overLimit: boolean } {
  // Pre-flight estimate — bail before polygonToCells can lock the main thread
  if (estimateCellsForBox(box, resolution) > MAX_CELLS) {
    return { cells: [], overLimit: true };
  }
  const polygon: [number, number][] = [
    [box.north, box.west],
    [box.north, box.east],
    [box.south, box.east],
    [box.south, box.west],
  ];
  const cells = polygonToCells(polygon, resolution);
  if (cells.length > MAX_CELLS) return { cells: [], overLimit: true };
  return { cells, overLimit: false };
}

/**
 * Approximate lat/lng box for a 3D camera (top-down assumption). Map3DElement
 * has no public getBounds(); we derive a box from center + range — visible
 * half-width ≈ range * 0.5 m at tilt=0. Shared by H3 overlay + viewport culling.
 */
export function boxForCamera3D(
  center: { lat: number; lng: number },
  range: number,
): { north: number; south: number; east: number; west: number } {
  const radiusMeters = range * 0.5;
  const latDelta = Math.min(radiusMeters / 111_000, 80);
  const lngDelta = Math.min(
    latDelta / Math.max(Math.cos((center.lat * Math.PI) / 180), 0.01),
    180,
  );
  return {
    north: Math.min(center.lat + latDelta, 89),
    south: Math.max(center.lat - latDelta, -89),
    east: center.lng + lngDelta,
    west: center.lng - lngDelta,
  };
}

/** Approximate viewport cells for a 3D camera (reuses {@link boxForCamera3D}). */
export function cellsForCamera3D(
  center: { lat: number; lng: number },
  range: number,
  resolution: number,
): { cells: string[]; overLimit: boolean } {
  return cellsForBox(boxForCamera3D(center, range), resolution);
}

/** Polygon path (Google Maps shape) for an H3 cell boundary */
export function cellToPath(cell: string): google.maps.LatLngLiteral[] {
  return cellToBoundary(cell).map(([lat, lng]) => ({ lat, lng }));
}

/** Count of events per cell at a given resolution */
export function densityByCell(
  events: NormalizedSaEvent[],
  resolution: number,
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const evt of events) {
    const cell = latLngToCell(evt.lat, evt.lon, resolution);
    counts.set(cell, (counts.get(cell) ?? 0) + 1);
  }
  return counts;
}

/** Cell containing the given coordinate */
export function cellAt(lat: number, lng: number, resolution: number): string {
  return latLngToCell(lat, lng, resolution);
}

/** Events whose position falls inside the given cell */
export function eventsInCell(
  events: NormalizedSaEvent[],
  cell: string,
  resolution: number,
): NormalizedSaEvent[] {
  return events.filter(
    (evt) => latLngToCell(evt.lat, evt.lon, resolution) === cell,
  );
}

/** Approximate edge length for a resolution, for UI display */
export function resolutionLabel(res: number): string {
  const labels: Record<number, string> = {
    4: '~23 km',
    5: '~8.5 km',
    6: '~3.2 km',
    7: '~1.2 km',
    8: '~460 m',
    9: '~175 m',
    10: '~65 m',
    11: '~25 m',
  };
  return labels[res] ?? '—';
}
